/**
 * Analytics event helper — fires the same event to both Meta Pixel (fbq)
 * and Google Analytics 4 (gtag). Safe to call before either script has loaded.
 *
 * All money values are passed in INR (rupees), not paise.
 */

// Map our common event name -> {fbEvent, gaEvent}
const EVENT_MAP = {
  ViewContent:      { fb: "ViewContent",      ga: "view_item" },
  AddToCart:        { fb: "AddToCart",        ga: "add_to_cart" },
  InitiateCheckout: { fb: "InitiateCheckout", ga: "begin_checkout" },
  Purchase:         { fb: "Purchase",         ga: "purchase" },
  Search:           { fb: "Search",           ga: "search" },
};

function paiseToRupees(n) {
  return typeof n === "number" ? Math.round(n) / 100 : 0;
}

/**
 * @param {"ViewContent"|"AddToCart"|"InitiateCheckout"|"Purchase"|"Search"} name
 * @param {object} data - See per-event fields below.
 *
 * Event data shape (all monetary values in PAISE, we convert to INR for pixel/GA):
 *   ViewContent:      { product_id, name, category, price }
 *   AddToCart:        { product_id, name, category, price, qty }
 *   InitiateCheckout: { total, items: [{product_id, name, qty, price}] }
 *   Purchase:         { order_id, total, items: [...], payment_method?, coupon? }
 */
export function track(name, data = {}) {
  const map = EVENT_MAP[name];
  if (!map) return;

  // ---- Meta Pixel ----
  if (typeof window.fbq === "function") {
    try {
      const fbPayload = buildFbPayload(name, data);
      window.fbq("track", map.fb, fbPayload);
    } catch (e) { /* swallow */ }
  }

  // ---- GA4 ----
  if (typeof window.gtag === "function") {
    try {
      const gaPayload = buildGaPayload(name, data);
      window.gtag("event", map.ga, gaPayload);
    } catch (e) { /* swallow */ }
  }
}

function buildFbPayload(name, d) {
  const currency = "INR";
  if (name === "ViewContent") {
    return {
      content_type: "product",
      content_ids: [d.product_id],
      content_name: d.name,
      content_category: d.category,
      value: paiseToRupees(d.price),
      currency,
    };
  }
  if (name === "AddToCart") {
    return {
      content_type: "product",
      content_ids: [d.product_id],
      content_name: d.name,
      content_category: d.category,
      value: paiseToRupees(d.price) * (d.qty || 1),
      currency,
      num_items: d.qty || 1,
    };
  }
  if (name === "InitiateCheckout") {
    return {
      content_type: "product",
      content_ids: (d.items || []).map((i) => i.product_id),
      contents: (d.items || []).map((i) => ({ id: i.product_id, quantity: i.qty })),
      num_items: (d.items || []).reduce((s, i) => s + i.qty, 0),
      value: paiseToRupees(d.total),
      currency,
    };
  }
  if (name === "Purchase") {
    return {
      content_type: "product",
      content_ids: (d.items || []).map((i) => i.product_id),
      contents: (d.items || []).map((i) => ({ id: i.product_id, quantity: i.qty })),
      num_items: (d.items || []).reduce((s, i) => s + i.qty, 0),
      value: paiseToRupees(d.total),
      currency,
    };
  }
  if (name === "Search") return { search_string: d.q };
  return {};
}

function buildGaPayload(name, d) {
  const currency = "INR";
  const item = (i) => ({
    item_id: i.product_id,
    item_name: i.name,
    quantity: i.qty || 1,
    price: paiseToRupees(i.price),
  });
  if (name === "ViewContent") {
    return {
      currency, value: paiseToRupees(d.price),
      items: [{
        item_id: d.product_id, item_name: d.name,
        item_category: d.category, price: paiseToRupees(d.price), quantity: 1,
      }],
    };
  }
  if (name === "AddToCart") {
    return {
      currency, value: paiseToRupees(d.price) * (d.qty || 1),
      items: [{
        item_id: d.product_id, item_name: d.name,
        item_category: d.category, price: paiseToRupees(d.price), quantity: d.qty || 1,
      }],
    };
  }
  if (name === "InitiateCheckout") {
    return { currency, value: paiseToRupees(d.total), items: (d.items || []).map(item) };
  }
  if (name === "Purchase") {
    return {
      transaction_id: d.order_id,
      currency,
      value: paiseToRupees(d.total),
      coupon: d.coupon,
      payment_type: d.payment_method,
      items: (d.items || []).map(item),
    };
  }
  if (name === "Search") return { search_term: d.q };
  return {};
}
